import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "../../lib/api";

/**
 * KaizenIntakeLog — permanent, always-on record of every Kaizen email the
 * intake pipeline has seen, so an offer going from "received" to "no longer
 * available" is never invisible again (fixes the #3868722 silent-drop gap).
 * Reads the existing GET /api/kaizen/email-trigger/timeline — no new backend
 * surface needed, the data was already being written, just never shown.
 */
const OUTCOME_STYLE = {
  accepted: { label: "WON", cls: "kil-pill--won" },
  voice_auth_approved: { label: "WON", cls: "kil-pill--won" },
  gone_before_arrival: { label: "LOST RACE", cls: "kil-pill--lost" },
  partner_leg_gone: { label: "LOST RACE", cls: "kil-pill--lost" },
  skipped_expired_window: { label: "MISSED WINDOW", cls: "kil-pill--missed" },
  rule_skipped: { label: "RULE SKIP", cls: "kil-pill--skip" },
  // "accept_failed" kept for backward compat with historical records
  // written before the 2026-10-15 split into confirmed_gone/accept_error.
  accept_failed: { label: "DECLINED", cls: "kil-pill--skip" },
  confirmed_gone: { label: "LOST RACE", cls: "kil-pill--lost" },
  accept_error: { label: "RETRY FAILED", cls: "kil-pill--skip" },
  voice_auth_declined: { label: "DECLINED", cls: "kil-pill--skip" },
  voice_auth_no_answer: { label: "NO ANSWER", cls: "kil-pill--skip" },
  voice_auth_held_cooldown: { label: "HELD", cls: "kil-pill--skip" },
  voice_auth_skipped: { label: "SKIPPED", cls: "kil-pill--skip" },
};

function outcomeBadge(outcome) {
  return OUTCOME_STYLE[outcome] || { label: outcome || "unknown", cls: "kil-pill--unknown" };
}

function ageLabel(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.round(m / 60)}h ago`;
}

export default function KaizenIntakeLog() {
  const [data, setData] = useState(null);
  // Defaults to collapsed (2026-10-15 fix) — a log/history view, not an
  // action-required panel; one click away via "expand" instead of
  // crowding the rail by default.
  const [collapsed, setCollapsed] = useState(true);
  const [error, setError] = useState(false);
  const [expandedRide, setExpandedRide] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await apiClient.get("/kaizen/email-trigger/timeline?limit=40");
      setData(data);
      setError(false);
    } catch {
      setError(true);
    }
  };

  const toggleRow = async (rideId) => {
    if (!rideId) return;
    if (expandedRide === rideId) {
      setExpandedRide(null);
      setTimeline(null);
      return;
    }
    setExpandedRide(rideId);
    setTimeline(null);
    setTimelineLoading(true);
    try {
      const { data } = await apiClient.get(`/kaizen/offer-timeline/${rideId}`);
      setTimeline(data);
    } catch {
      setTimeline({ error: true });
    } finally {
      setTimelineLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const heartbeatGapMin = useMemo(() => {
    if (!data?.last_heartbeat_at) return null;
    return (Date.now() - new Date(data.last_heartbeat_at).getTime()) / 60000;
  }, [data]);
  const watcherStale = heartbeatGapMin != null && heartbeatGapMin > 2;
  const replicas = data?.replicas || [];
  const staleReplicas = useMemo(
    () => replicas.filter((r) => (Date.now() - new Date(r.last_tick_at).getTime()) / 60000 > 3),
    [replicas]
  );

  const rows = data?.rows || [];
  const summary = data?.summary || {};

  return (
    <aside className={`kaizen-log ${collapsed ? "kaizen-log--collapsed" : ""}`} data-testid="kaizen-intake-log">
      <header className="kil-head">
        <div className="kil-title">
          <span className="kil-dot" />
          <span className="kil-label">KAIZEN · INTAKE LOG</span>
          <span className="kil-count" data-testid="kaizen-intake-log-count">{rows.length}</span>
        </div>
        <button className="il-collapse" onClick={() => setCollapsed((c) => !c)} data-testid="kaizen-intake-log-toggle">
          {collapsed ? "expand" : "collapse"}
        </button>
      </header>

      {!collapsed && (
        <>
          {watcherStale && (
            <div className="kil-stale-banner" data-testid="kaizen-watcher-stale-banner">
              ⚠ watcher last checked in {Math.round(heartbeatGapMin)}m ago — emails may be waiting
            </div>
          )}
          {staleReplicas.length > 0 && (
            <div className="kil-stale-banner" data-testid="kaizen-watcher-replica-stale-banner">
              ⚠ {staleReplicas.length} of {replicas.length} watcher replica(s) gone quiet — others are still running
            </div>
          )}
          {error && (
            <div className="kil-stale-banner" data-testid="kaizen-intake-log-error">
              ⚠ could not load intake log
            </div>
          )}
          <div className="kil-summary" data-testid="kaizen-intake-log-summary">
            <span>{summary.emails_received_24h ?? "—"} emails/24h</span>
            <span>{summary.pending_retries_count ?? 0} pending retry</span>
            <span>{summary.avg_window_seconds != null ? `${summary.avg_window_seconds}s avg window` : "—"}</span>
            <span>{replicas.length || 1} watcher{replicas.length === 1 ? "" : "s"} live</span>
          </div>
          <div className="kil-list">
            {rows.length === 0 ? (
              <div className="il-empty" data-testid="kaizen-intake-log-empty">
                <div className="il-empty-line">no Kaizen emails logged yet</div>
              </div>
            ) : (
              rows.map((r, i) => {
                const badge = outcomeBadge(r.outcome);
                const rideId = r.ride_id || r.kaizen_offer_id;
                const isOpen = expandedRide === rideId;
                return (
                  <React.Fragment key={`${rideId || i}-${r.checked_at}`}>
                    <div
                      className="kil-row kil-row--clickable"
                      data-testid={`kaizen-log-row-${r.ride_id || i}`}
                      onClick={() => toggleRow(rideId)}
                    >
                      <span className={`kil-pill ${badge.cls}`}>{badge.label}</span>
                      <span className="kil-ride">#{rideId || "—"}</span>
                      <span className="kil-name">{r.patient_name || "—"}</span>
                      {r.pickup_state && r.pickup_state !== "IL" && (
                        <span className="kil-state" data-testid={`kaizen-log-state-${rideId}`}>{r.pickup_state}</span>
                      )}
                      <span className="kil-age" title={r.email_received_at}>{ageLabel(r.email_received_at)}</span>
                      {typeof r.window_seconds === "number" && (
                        <span className="kil-window">{Math.round(r.window_seconds)}s</span>
                      )}
                    </div>
                    {isOpen && (
                      <div className="kil-timeline" data-testid={`kaizen-log-timeline-${rideId}`}>
                        {timelineLoading && <div className="kil-timeline-loading">loading full timeline…</div>}
                        {timeline?.error && <div className="kil-timeline-loading">could not load timeline</div>}
                        {timeline && !timeline.error && (timeline.stages || []).map((s, si) => (
                          <div className="kil-timeline-row" key={si}>
                            <span className="kil-timeline-elapsed">
                              {s.elapsed_s != null ? `T+${s.elapsed_s}s` : "—"}
                            </span>
                            <span className="kil-timeline-label">{s.label}</span>
                          </div>
                        ))}
                        {timeline && !timeline.error && (timeline.stages || []).length === 0 && (
                          <div className="kil-timeline-loading">no stage data recorded for this offer</div>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </div>
        </>
      )}
    </aside>
  );
}
